import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { enqueueSnackbar } from 'notistack';
import { useCompanyMutation } from '../hooks/useCompanyMutation';
import { useGetTaxBenefitProviders } from '../hooks/useTaxBenefitProviders';
import { TaxBenefitsReviewMode } from '../types';
import {
  CompanyBasicSettingsForm,
  CompanyBasicSettingsFormData,
} from './CompanyBasicSettingsForm';
import {
  CompanyTaxBenefitsForm,
  TaxBenefitsFormData,
} from './CompanyTaxBenefitsForm';

export function OrganizationDetailsTab() {
  const { employer, isLoading: isLoadingUserContext } =
    useCurrentUserContext() ?? {};
  const { mutateAsync: updateCompany } = useCompanyMutation(
    employer?._id ?? '',
  );

  const {
    isLoading: isLoadingTaxBenefitsProviders,
    data: { data: taxBenefitsProviders = [] } = {},
  } = useGetTaxBenefitProviders({}, { page: 1, limit: 5000 });

  const handleCompanyUpdate = async (
    updatedData: CompanyBasicSettingsFormData,
  ) => {
    try {
      await updateCompany({
        updatedData,
      });
      enqueueSnackbar('Company settings updated successfully', {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar('Failed to update company settings', {
        variant: 'error',
      });
      throw new Error('Failed to update company settings');
    }
  };

  const handleTaxBenefitsUpdate = async (data: TaxBenefitsFormData) => {
    try {
      await updateCompany({
        id: employer?._id ?? '',
        updatedData: {
          taxBenefitsProvider:
            data.taxBenefitsReviewMode === TaxBenefitsReviewMode.Provider
              ? data.taxBenefitsProvider?._id ?? null
              : null,
          settings: {
            ...employer?.settings,
            skipTaxBenefitsProviderValidation:
              data.taxBenefitsReviewMode === TaxBenefitsReviewMode.Independent,
          },
        },
      });
      enqueueSnackbar('Tax benefits settings updated successfully', {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar('Failed to update tax benefits settings', {
        variant: 'error',
      });
      throw new Error('Failed to update tax benefits settings');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <CompanyBasicSettingsForm
        className="mt-8"
        initialData={{
          name: employer?.name ?? '',
          legalName: employer?.legalName ?? '',
          address: employer?.address ?? '',
          domain: employer?.domain ?? '',
          alternativeDomains: employer?.alternativeDomains ?? [],
        }}
        onSubmit={handleCompanyUpdate}
        isLoading={isLoadingUserContext}
      />
      <CompanyTaxBenefitsForm
        className="mt-10"
        initialData={{
          taxBenefitsProvider: employer?.taxBenefitsProvider ?? null,
          taxBenefitsReviewMode: employer?.settings
            ?.skipTaxBenefitsProviderValidation
            ? TaxBenefitsReviewMode.Independent
            : TaxBenefitsReviewMode.Provider,
        }}
        onSubmit={handleTaxBenefitsUpdate}
        isLoading={isLoadingUserContext || isLoadingTaxBenefitsProviders}
        taxBenefitsProviders={taxBenefitsProviders}
      />
    </div>
  );
}
